import React, { useState, useEffect } from 'react';
import { getSchemaForType } from '../constants/fieldSchemas';
import { compareFields } from '../utils/normalize';
import { renderHighlightedText } from '../utils/highlightDiff';
import { AlertTriangle, CheckCircle, Edit2, ChevronDown, ChevronUp } from 'lucide-react';

export default function CompareResultPanel({ documentType, flashData, proData, onProceed }) {
  console.log('[CompareResultPanel] Received props:', {
    documentType,
    flashDataType: flashData?.documentType,
    proDataType: proData?.documentType,
    hasFlashData: !!flashData,
    hasProData: !!proData
  });

  // 모든 Hooks는 조건부 return 전에 호출 (React 규칙)
  const schema = React.useMemo(() => getSchemaForType(documentType), [documentType]);
  const { rows, matched, total, mismatched, rate } = React.useMemo(
    () => compareFields(flashData, proData, schema),
    [flashData, proData, schema]
  );

  // 편집 가능한 최종 데이터 상태
  const [editedData, setEditedData] = useState(() => {
    const initial = {
      documentType: documentType // documentType은 prop에서 직접 사용
    };
    rows.forEach(r => {
      // Pro 우선, 없으면 Flash
      initial[r.key] = r.pro !== null && r.pro !== undefined ? r.pro : r.flash;
    });
    return initial;
  });

  // documentType이나 rows가 변경되면 editedData 업데이트
  useEffect(() => {
    const updated = {
      documentType: documentType
    };
    rows.forEach(r => {
      updated[r.key] = r.pro !== null && r.pro !== undefined ? r.pro : r.flash;
    });
    setEditedData(updated);
  }, [documentType, flashData, proData, rows]);

  // 각 필드의 차이 표시 열림/닫힘 상태
  const [expandedFields, setExpandedFields] = useState(() => {
    const initial = {};
    rows.forEach(r => {
      if (!r.match) {
        initial[r.key] = true; // 차이 있는 필드는 기본 열림
      }
    });
    return initial;
  });

  const allMatch = total > 0 && matched === total;
  const needsReview = rate < 90; // 90% 미만이면 검토 필요

  // 필드 값 변경 핸들러
  const handleFieldChange = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Flash 값 선택
  const selectFlashValue = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Pro 값 선택
  const selectProValue = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 차이 표시 토글
  const toggleExpanded = (key) => {
    setExpandedFields(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // 최종 확인
  const handleConfirm = () => {
    // 편집된 데이터를 병합하여 전달
    // 일치율 계산 (editedData 기준으로 Flash/Pro와 비교)
    let matchedFields = 0;
    let totalFields = rows.length;

    rows.forEach(row => {
      const editedValue = editedData[row.key];
      // 둘 다 같으면 일치
      if (editedValue === row.flash || editedValue === row.pro) {
        matchedFields++;
      }
    });

    const matchRate = totalFields > 0 ? Math.round((matchedFields / totalFields) * 100) : 100;

    const finalData = {
      ...proData,
      ...flashData,
      ...editedData,
      metadata: {
        ...proData?.metadata,
        ...flashData?.metadata,
        matchRate: matchRate,
        userEdited: true,
        editedAt: new Date().toISOString(),
        flashData: flashData,
        proData: proData
      }
    };
    onProceed(finalData);
  };

  // 데이터가 없으면 처리 대기 중 상태 표시
  if (!flashData && !proData) {
    return (
      <div className="h-full flex flex-col bg-white overflow-hidden">
        <div className="flex-shrink-0 p-3 border-b border-gray-200" style={{ backgroundColor: '#F5F5F7' }}>
          <h2 className="font-bold text-gray-700 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            문서 처리 대기 중...
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-sm text-gray-600 space-y-2">
              <p>⏳ Smart Process를 실행하면</p>
              <p className="font-semibold">실시간으로 결과가 표시됩니다</p>
              <div className="mt-4 text-xs text-gray-500">
                분류 → 추출 → 검증
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* 헤더: 일치율 표시 */}
      <div className="p-4 border-b border-gray-200" style={{
        backgroundColor: allMatch ? '#F5F5F7' : needsReview ? '#F5F5F7' : '#F5F5F7'
      }}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            {allMatch ? (
              <>
                <CheckCircle className="w-5 h-5" style={{ color: '#1EE9B6' }} />
                검증 완료
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5" style={{ color: needsReview ? '#FFDD33' : '#6B7280' }} />
                검증 결과 - 수정하세요
              </>
            )}
          </h2>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${allMatch ? 'bg-gray-700 text-white' :
              needsReview ? 'text-[#1C1C1E]' :
                'bg-gray-700 text-white'
              }`} style={needsReview ? { backgroundColor: '#FFDD33' } : {}}>
              일치율 {rate}%
            </span>
          </div>
        </div>
        <div className="text-sm" style={{ color: '#0A1037' }}>
          {allMatch ? (
            <span className="font-semibold">✅ 모든 필드가 일치합니다 ({matched}/{total})</span>
          ) : (
            <span>일치 {matched}개 · 불일치 {mismatched}개 (총 {total}개 필드)</span>
          )}
        </div>
      </div>

      {/* 편집 가능한 필드 리스트 - 심플 헤더 형식 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {rows.map((r) => {
          const isExpanded = expandedFields[r.key];
          const hasDifference = !r.match;
          const currentValue = editedData[r.key] ?? '';

          return (
            <div
              key={r.key}
              className={`flex items-center gap-2 py-2 px-3 rounded ${hasDifference ? 'bg-[#FFF9E6]' : 'bg-white'
                }`}
            >
              {/* 필드명: 값 형식 */}
              <div className="flex items-center gap-2 flex-1">
                <label className="text-sm font-medium text-gray-700 whitespace-pre-wrap min-w-[120px]">
                  {r.label}:
                </label>
                {r.key === 'documentType' ? (
                  <select
                    value={currentValue}
                    onChange={(e) => handleFieldChange(r.key, e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">문서 타입 선택</option>
                    <option value="경락확인서">경락확인서</option>
                    <option value="자동차매매계약서">자동차매매계약서</option>
                    <option value="자동차등록원부">자동차등록원부</option>
                    <option value="자동차등록증">자동차등록증</option>
                    <option value="말소등록증">말소등록증</option>
                    <option value="사업자등록증">사업자등록증</option>
                    <option value="신분증">신분증</option>
                    <option value="폐차증">폐차증</option>
                    <option value="세금계산서">세금계산서</option>
                    <option value="기타">기타</option>
                  </select>
                ) : (
                  <div className="flex-1">
                    {hasDifference ? (
                      <div className="px-2 py-1 border border-gray-300 rounded text-sm bg-white">
                        {renderHighlightedText(r.flash, r.pro)}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => handleFieldChange(r.key, e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="값을 입력하세요"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* 차이 표시 */}
              {hasDifference && (
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold text-[#1C1C1E] bg-[#FFDD33]">
                    차이
                  </span>
                  <button
                    onClick={() => toggleExpanded(r.key)}
                    className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                  >
                    {isExpanded ? '상세 닫기' : '상세 보기'}
                  </button>
                </div>
              )}

              {/* 차이 비교 영역 (확장 시) */}
              {hasDifference && isExpanded && (
                <div className="col-span-full px-3 py-2 bg-gray-50 rounded text-xs space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-600 min-w-[80px]">
                      {flashData?.source === 'claude-sonnet-4-5' ? 'Sonnet 4.5:' :
                        flashData?.source === 'gemini-3-pro-preview' ? 'Gemini 3.0 Pro:' :
                          flashData?.source === 'gemini-2.5-pro' ? 'Gemini 2.5 Pro:' :
                            flashData?.source === 'gemini-2.5-flash' ? 'Gemini 2.5 Flash:' :
                              '모델 1:'}
                    </span>
                    <button
                      onClick={() => selectFlashValue(r.key, r.flash)}
                      className="text-blue-600 hover:underline text-left"
                    >
                      {renderHighlightedText(r.flash, r.pro)}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-600 min-w-[80px]">
                      {proData?.source === 'claude-sonnet-4-5' ? 'Sonnet 4.5:' :
                        proData?.source === 'gemini-3-pro-preview' ? 'Gemini 3.0 Pro:' :
                          proData?.source === 'gemini-2.5-pro' ? 'Gemini 2.5 Pro:' :
                            proData?.source === 'gemini-2.5-flash' ? 'Gemini 2.5 Flash:' :
                              '모델 2:'}
                    </span>
                    <button
                      onClick={() => selectProValue(r.key, r.pro)}
                      className="text-green-600 hover:underline text-left"
                    >
                      {renderHighlightedText(r.pro, r.flash)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 하단 버튼 */}
      <div className="p-4 border-t border-gray-200" style={{ backgroundColor: '#F4F4F4' }}>
        <button
          onClick={handleConfirm}
          className="w-full px-4 py-3 text-sm font-bold rounded shadow-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          style={{ backgroundColor: '#6B7280', color: '#FFFFFF' }}
        >
          <CheckCircle className="w-4 h-4" />
          검증 완료 - 시스템 입력
        </button>
        <p className="text-xs text-center mt-2" style={{ color: '#0A1037' }}>
          💡 수정된 데이터가 최종 입력됩니다
        </p>
      </div>
    </div>
  );
}
